import * as React from "react";
import { Branch, XtrfService } from "gen/clients";
import { DataTable } from "components/DataTable/DataTable";
import { DataTableColumn, DataTableRowAction } from "components/DataTable/types";
import { useDialogState } from "hooks/useDialogState";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { ConfirmationDialog } from "components/ConfirmationDialog/ConfirmationDialog";

interface Props {
  branches: Branch[];
  xtrfServices?: XtrfService[];
  onEditClick: (branch: Branch) => void;
  onDeleteClick: (branch: Branch) => Promise<void>;
  isUpdating: boolean;
  updateError: any;
}

const BranchesTable: React.FC<Props> = ({
  branches,
  xtrfServices = [],
  onEditClick,
  onDeleteClick,
  isUpdating,
  updateError
}) => {
  const [deleteDialogOpen, openDeleteDialog, closeDeleteDialog] = useDialogState();
  const [branchToDelete, setBranchToDelete] = React.useState<Branch | null>(null);

  const handleDeleteClick = React.useCallback(
    (branch: Branch) => {
      setBranchToDelete(branch);
      openDeleteDialog();
    },
    [openDeleteDialog]
  );

  const confirmDelete = async () => {
    if (branchToDelete) {
      try {
        await onDeleteClick(branchToDelete);
        if (!updateError) {
          closeDeleteDialog();
        }
      } catch (_error) {
        // Error is handled by the parent component
      }
    }
  };

  const columns: DataTableColumn[] = [
    {
      id: "id",
      title: "Branch ID",
      cell: (branch: Branch) => branch.id || "Not set"
    },
    {
      id: "name",
      title: "Branch Name",
      cell: (branch: Branch) => branch.name
    },
    {
      id: "xtrfId",
      title: "XTRF ID",
      cell: (branch: Branch) => branch.xtrfId
    },
    {
      id: "currency",
      title: "Currency",
      cell: (branch: Branch) => branch.currency || "Not set"
    },
    {
      id: "supportEmail",
      title: "Customer Support Email Address",
      cell: (branch: Branch) => {
        if (!branch.supportEmail) {
          return "Not set";
        }
        return (
          <a href={`mailto:${branch.supportEmail}`} style={{ color: "inherit", textDecoration: "underline" }}>
            {branch.supportEmail}
          </a>
        );
      }
    },
    {
      id: "phoneNumber",
      title: "Phone Number",
      cell: (branch: Branch) => branch.phoneNumber || "Not set"
    },
    {
      id: "websiteUrl",
      title: "Website URL",
      cell: (branch: Branch) => {
        if (!branch.websiteUrl) {
          return "Not set";
        }
        return (
          <a
            href={branch.websiteUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "inherit", textDecoration: "underline" }}
          >
            {branch.websiteUrl}
          </a>
        );
      }
    },
    {
      id: "xtrfServiceId",
      title: "Default XTRF Service",
      cell: (branch: Branch) => {
        if (!branch.xtrfServiceId) {
          return "Not assigned";
        }
        const service = xtrfServices.find(s => s.id === branch.xtrfServiceId);
        return service ? service.name : "Unknown service";
      }
    }
  ];

  const rowActions = React.useCallback(
    (branch: Branch): DataTableRowAction[] => [
      {
        title: "Edit",
        action: () => onEditClick(branch)
      },
      {
        title: "Delete",
        action: () => handleDeleteClick(branch),
        color: "error.main"
      }
    ],
    [onEditClick, handleDeleteClick]
  );

  const rowKey = React.useCallback((branch: Branch) => branch.id || branch.xtrfId.toString(), []);

  return (
    <>
      <DataTable
        columns={columns}
        data={branches}
        rowKey={rowKey}
        rowActions={rowActions}
        dense={true}
        noData={
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No branches have been added yet. Click "Add Branch" to create your first branch.
            </Typography>
          </Box>
        }
      />

      {deleteDialogOpen && branchToDelete && (
        <ConfirmationDialog
          title="Confirm Delete"
          content={
            <>Are you sure you want to delete the branch "{branchToDelete.name}"? This action cannot be undone.</>
          }
          onClose={closeDeleteDialog}
          onConfirm={confirmDelete}
          confirmationButtonColor="error"
          isLoading={isUpdating}
          error={updateError}
        />
      )}
    </>
  );
};

export { BranchesTable };
